// src/pages/Charts.js

import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from "recharts";
import { Helmet } from "react-helmet";

function Charts() {
  const { genre } = useParams();
  const [artists, setArtists] = useState([]);
  const [hoveredAlbums, setHoveredAlbums] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (!genre) return;

    setArtists([]);
    setLoading(true);
    setError(null);

    axios
      .get(`https://critica-backend.onrender.com/genre/${genre}`)
      .then((res) => {
        let cleaned = res.data
          .map((artist) => {
            const albums = artist.albums;

            const totalScore = albums.reduce((sum, a) => {
              const p = parseFloat(a.pitchfork);
              const m = parseFloat(a.metacritic);
              return sum + (isNaN(p) ? 0 : p) + (isNaN(m) ? 0 : m);
            }, 0);

            const validCount = albums.reduce((count, a) => {
              const p = parseFloat(a.pitchfork);
              const m = parseFloat(a.metacritic);
              return count + (isNaN(p) ? 0 : 1) + (isNaN(m) ? 0 : 1);
            }, 0);

            const avgScore = validCount > 0 ? totalScore / validCount : 0;

            return {
              ...artist,
              albums: albums
  .map((a) => {
    const releaseYear = parseInt(a.release_year);
    return {
      ...a,
      album: a.album || "Unknown Album",
      release_year: isNaN(releaseYear) ? null : releaseYear,
      pitchfork: isNaN(parseFloat(a.pitchfork)) ? null : parseFloat(a.pitchfork),
      metacritic: isNaN(parseFloat(a.metacritic)) ? null : parseFloat(a.metacritic),
      fantano: isNaN(parseFloat(a.fantano)) ? null : parseFloat(a.fantano),
    };
  })
  .filter((a) => a.release_year !== null),
              _totalScore: totalScore,
              _avgScore: avgScore,
            };
          })
          .filter(Boolean);

        setArtists(cleaned);
        setLoading(false);
      })
      .catch((err) => {
        console.error("❌ API Error:", err);
        setError("Failed to load data. Try again.");
        setLoading(false);
      });
  }, [genre]);

  const linkStyle = {
    display: "block",
    padding: "10px 20px",
    textDecoration: "none",
    backgroundColor: "transparent",
    color: "white",
    fontSize: "16px",
    width: "100%",
    textAlign: "left",
    fontFamily: "'Rubik', sans-serif",
  };

  return (
    <div
      style={{
        backgroundColor: "#121212",
        minHeight: "100vh",
        padding: "20px",
        fontFamily: "'Rubik', sans-serif",
        color: "#EAEAEA",
      }}
    >
      <Helmet>
        <link href="https://fonts.googleapis.com/css2?family=Rubik&display=swap" rel="stylesheet" />
        <link href="https://fonts.googleapis.com/css2?family=Roboto&display=swap" rel="stylesheet" />
      </Helmet>

      <header
        style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "1fr 2fr 1fr",
          alignItems: "center",
          padding: "20px 20px",
          borderBottom: "2px solid #1E1E1E",
        }}
      >
        <h1
          style={{
            fontSize: "45px",
            fontWeight: "bold",
            color: "#04CD46",
            fontFamily: "'Rubik', sans-serif",
            textAlign: "left",
            paddingLeft: "20px",
            textTransform: "lowercase",
          }}
        >
          critica
        </h1>

        <nav style={{ display: "flex", justifyContent: "center", gap: "18px", position: "relative" }}>
          <Link to="/" style={{ textDecoration: "none", color: "white", fontSize: "18px", fontWeight: "bold" }}>
            HOME
          </Link>

          <div
            style={{ position: "relative", display: "inline-block" }}
            onMouseEnter={() => setShowDropdown(true)}
            onMouseLeave={() => setShowDropdown(false)}
          >
            <span
              role="button"
              tabIndex={0}
              style={{
                textDecoration: "none",
                color: "white",
                fontSize: "18px",
                fontWeight: "bold",
                cursor: "pointer",
              }}
              onClick={() => setShowDropdown((prev) => !prev)}
            >
              CHARTS ▼
            </span>

            {showDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: "0",
                  backgroundColor: "#1E1E1E",
                  borderRadius: "5px",
                  overflow: "hidden",
                  boxShadow: "0px 4px 10px rgba(0,0,0,0.3)",
                  zIndex: "100",
                }}
              >
                {["rap", "pop"].map((genre) => (
                  <Link
                    key={genre}
                    to={`/charts/${genre}`}
                    style={linkStyle}
                    onMouseEnter={(e) => (e.target.style.backgroundColor = "#333")}
                    onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                  >
                    {genre.charAt(0).toUpperCase() + genre.slice(1)}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/compare" style={{ textDecoration: "none", color: "white", fontSize: "18px", fontWeight: "bold" }}>
            COMPARE
          </Link>
        </nav>
      </header>

      {loading && <p style={{ textAlign: "center", fontSize: "18px" }}>Loading data...</p>}
      {error && <p style={{ textAlign: "center", fontSize: "18px", color: "red" }}>{error}</p>}
      {!loading && !error && artists.length === 0 && (
        <p style={{ textAlign: "center", fontSize: "18px" }}>No data available for this genre.</p>
      )}

      {artists.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "25px",
            marginTop: "20px",
            maxWidth: "1200px",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {[...artists]
            .sort((a, b) => b._totalScore - a._totalScore || b._avgScore - a._avgScore)
            .map((artist, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: "#1E1E1E",
                  padding: "20px",
                  borderRadius: "10px",
                  textAlign: "center",
                  maxWidth: "350px",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "space-between",
                  position: "relative",
                }}
              >
                <h3 style={{ fontSize: "20px", fontFamily: "'Rubik', sans-serif", marginBottom: "5px" }}>
                  {artist.artist}
                </h3>

                <div style={{ width: "100%", height: "200px", padding: "0 10px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={artist.albums}
                      margin={{ top: 10, right: 20, bottom: 40, left: 20 }}
                      onMouseMove={(e) => {
                        const point = e?.activePayload?.[0]?.payload;
                        if (point) {
                          setHoveredAlbums((prev) => ({
                            ...prev,
                            [artist.artist]: point,
                          }));
                        }
                      }}
                      onMouseLeave={() =>
                        setHoveredAlbums((prev) => ({
                          ...prev,
                          [artist.artist]: null,
                        }))
                      }
                    >
                      <XAxis
                        dataKey="release_year"
                        stroke="#EAEAEA"
                        tick={{ fontSize: 12, fontFamily: "Roboto, sans-serif" }}
                        interval={0}
                        tickFormatter={(_, index) => {
                          const years = artist.albums.map((a) => a.release_year);
                          const currentYear = artist.albums[index].release_year;
                          const firstIndex = years.indexOf(Math.min(...years));
                          const lastIndex = years.lastIndexOf(Math.max(...years));
                          return index === firstIndex || index === lastIndex ? currentYear : "";
                        }}
                      />
                      <YAxis
                        domain={[0, 10]}
                        stroke="#EAEAEA"
                        tick={{ fontSize: 12, fontFamily: "Roboto, sans-serif" }}
                        tickFormatter={(value) => (value === 0 || value === 10 ? value : "")}
                      />
                      <Line type="monotone" dataKey="pitchfork" stroke="#057EFC" strokeWidth={3.45} dot={false} connectNulls={false} />
                      <Line type="monotone" dataKey="metacritic" stroke="#04CD46" strokeWidth={3.45} dot={false} connectNulls={false} />
                      <Line type="monotone" dataKey="fantano" stroke="#FCDD0C" strokeWidth={3.45} dot={false} connectNulls={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {hoveredAlbums[artist.artist] && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "-20px",
                      width: "100%",
                      textAlign: "center",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      backgroundColor: "#1E1E1E",
                      padding: "5px",
                      borderRadius: "5px",
                    }}
                  >
                    <p style={{ color: "#EAEAEA", fontWeight: "bold", marginBottom: "2px" }}>
                      {hoveredAlbums[artist.artist]["Album"] || "Unknown Album"} (
                      {hoveredAlbums[artist.artist].release_year})
                    </p>
                    <p style={{ color: "#057EFC", margin: "2px 0" }}>
                      Pitchfork: {hoveredAlbums[artist.artist].pitchfork}
                    </p>
                    <p style={{ color: "#04CD46", margin: "2px 0" }}>
                      Metacritic: {hoveredAlbums[artist.artist].metacritic}
                    </p>
                    <p style={{ color: "#FCDD0C", margin: "2px 0" }}>
                      Fantano: {hoveredAlbums[artist.artist].fantano}
                    </p>
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default Charts;
